import { useState } from "react";
import { useViewer } from "../../../state/ViewerProvider";
import { downloadBlob } from "../../../util/download";
import { pngWithDpi } from "../../../util/imageMeta";
import { useExportSize } from "../useExportSize";
import { useSceneFileActions } from "../scene/useSceneFileActions";
import {
  CompactCheck,
  CompactNumber,
  CompactSelect,
  MiniBtn,
} from "../inspector/rows";

/**
 * Export left panel: Snapshot (size + format), 3D file (GLB ± Draco), the
 * whole scene (v65), and a turntable WebM recorder. Plain panel content; the
 * LeftPanel chrome supplies the header + scroll container. RPCs go straight to
 * `client.ring`.
 *
 * The older scene-settings downloads (.vjson, settings JSON) were removed at
 * the user's request (2026-10-08): the whole-scene file replaces both, and
 * "Open scene file" still opens files saved in either older format.
 */
export function ExportPanel() {
  const { client } = useViewer();
  const r = client.ring;

  // Follows RENDERER's Custom size; 1920x1080 only while no custom size is set.
  const { w, h, setW, setH, following } = useExportSize(1920, 1080);
  const [format, setFormat] = useState("png");
  const [transparent, setTransparent] = useState(false);
  // Print resolution written into the file. It used to exist only on the other
  // export panel and on the retired Canvas chrome, so looking for it here — the obvious place —
  // found nothing.
  const [dpi, setDpi] = useState(300);
  const [compress, setCompress] = useState(true);
  const [fps, setFps] = useState(30);
  const [duration, setDuration] = useState(6);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ key: string; message: string } | null>(null);

  /**
   * Every export here can genuinely fail — a 4K tiled snapshot can OOM, the
   * turntable recorder needs a codec the browser may not have, and any of them
   * reject outright if the viewer iframe is gone. The old version was
   * `try { await fn() } finally { setBusy(null) }` with every call site doing
   * `void run(...)`, so a rejection escaped as an unhandled promise rejection:
   * the label flipped back to "Download .glb", no file arrived, and the user
   * had no way to tell the export had failed. It also never checked the
   * in-flight flag, so a second click started a concurrent export whose
   * `finally` wiped the first one's indicator.
   */
  const run = async (key: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(key);
    setFailed(null);
    try {
      await fn();
    } catch (e) {
      setFailed({ key, message: e instanceof Error ? e.message : "Export failed." });
    } finally {
      setBusy(null);
    }
  };

  // Buttons other than the running one go disabled rather than merely inert, so
  // the user can see why a second export won't start.
  const other = (key: string) => busy !== null && busy !== key;

  return (
    <div className="flex flex-col gap-4 p-3">
      <Section title="Snapshot">
        <LabeledRow label="Size">
          <CompactNumber value={w} min={1} step={1} width={64} onChange={setW} />
          <span className="text-[12px] text-ed-text-3">×</span>
          <CompactNumber value={h} min={1} step={1} width={64} onChange={setH} />
        </LabeledRow>
        <LabeledRow label="Format">
          <CompactSelect
            value={format}
            onChange={setFormat}
            options={[
              { id: "png", label: "PNG" },
              { id: "jpeg", label: "JPEG" },
              { id: "webp", label: "WebP" },
            ]}
          />
        </LabeledRow>
        <LabeledRow label="Transparent">
          <CompactCheck checked={transparent} onChange={setTransparent} />
        </LabeledRow>
        <LabeledRow label="DPI (print)">
          <CompactNumber value={dpi} min={1} step={1} width={70} onChange={setDpi} />
        </LabeledRow>
        <p className="px-3 pb-1 text-[11px] leading-snug text-ed-text-3">
          Written into a .png so print software reads the right size. .jpeg / .webp carry no
          resolution tag, so there it only names the file.
          {following ? " Size is following the Renderer section — type here to override." : ""}
        </p>
        {/* Busy key is the constant "snapshot", not the mutable `format`: the
            Format select stays live during the render, so keying off `format`
            made `busy === format` go false the moment the user switched it and
            the only in-flight indicator vanished mid-render. `fmt` is captured
            at click time so a mid-flight switch can't rename the file either. */}
        <MiniBtn
          icon="download"
          primary
          busy={busy === "snapshot"}
          disabled={other("snapshot")}
          onClick={() => {
            const fmt = format as "png" | "jpeg" | "webp";
            void run("snapshot", async () => {
              const blob = await r.snapshot({ width: w, height: h, transparent, format: fmt });
              // Only PNG has somewhere to put a resolution tag (a pHYs chunk).
              const tagged = fmt === "png" ? await pngWithDpi(blob, dpi) : blob;
              downloadBlob(tagged, `jewelry_${w}x${h}_${dpi}dpi.${fmt === "jpeg" ? "jpg" : fmt}`);
            });
          }}
        >
          {busy === "snapshot" ? "Rendering…" : `Download .${format === "jpeg" ? "jpg" : format}`}
        </MiniBtn>
        <FailNote failed={failed} forKey="snapshot" />
      </Section>

      <Section title="3D File">
        <LabeledRow label="Compress (Draco)">
          <CompactCheck checked={compress} onChange={setCompress} />
        </LabeledRow>
        <MiniBtn
          icon="deployed_code"
          busy={busy === "glb"}
          disabled={other("glb")}
          onClick={() =>
            void run("glb", async () => {
              const blob = await r.exportGLB({ compress });
              downloadBlob(blob, compress ? "jewelry-export-compressed.glb" : "jewelry-export.glb");
            })
          }
        >
          {busy === "glb" ? "Exporting…" : "Download .glb"}
        </MiniBtn>
        <FailNote failed={failed} forKey="glb" />
      </Section>

      <WholeScene />

      <Section title="Turntable Video">
        <LabeledRow label="FPS">
          <CompactNumber value={fps} min={1} max={60} step={1} width={64} onChange={setFps} />
        </LabeledRow>
        <LabeledRow label="Duration (s)">
          <CompactNumber value={duration} min={1} max={60} step={1} width={64} onChange={setDuration} />
        </LabeledRow>
        <MiniBtn
          icon="movie"
          primary
          busy={busy === "video"}
          disabled={other("video")}
          onClick={() =>
            void run("video", async () => {
              const blob = await r.record360Video({ fps, duration, width: w, height: h });
              downloadBlob(blob, "turntable.webm");
            })
          }
        >
          {busy === "video" ? "Recording…" : "Download .webm"}
        </MiniBtn>
        <FailNote failed={failed} forKey="video" />
      </Section>
    </div>
  );
}

/**
 * Whole scene (viewer v65): everything except the ring in one `.jscene`, every
 * file packed inside; Import replaces the scene as one Undo step; and the ring
 * as a `.glb` with that scene inside. Absent on an older viewer — the methods
 * do not exist there, and RPC has no timeout.
 */
function WholeScene() {
  const a = useSceneFileActions();
  if (!a.available) return null;
  const other = (key: string) => !!a.busy && a.busy !== key;
  return (
    <Section title="Whole Scene">
      <div className="flex flex-wrap gap-1.5">
        <MiniBtn icon="save" primary busy={a.busy === "save"} disabled={other("save")} onClick={() => void a.save()}>
          {a.busy === "save" ? "Saving…" : "Save scene file"}
        </MiniBtn>
        <MiniBtn icon="folder_open" busy={a.busy === "open"} disabled={other("open")} onClick={() => void a.open()}>
          {a.busy === "open" ? "Opening…" : "Open scene file"}
        </MiniBtn>
      </div>
      <MiniBtn icon="deployed_code" busy={a.busy === "ring"} disabled={!a.hasModel || other("ring")} onClick={() => void a.ringWithScene()}>
        {a.busy === "ring" ? "Exporting…" : "Ring + scene (.glb)"}
      </MiniBtn>
      <p className="px-3 pb-1 text-[11px] leading-snug text-ed-text-3">
        Everything except the ring — lights, metals, stones, camera, effects, configurator — with every file packed
        inside. Opening one replaces the current look; you can undo it. Older .vjson and settings files open here
        too.
      </p>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[12px] font-semibold text-ed-text-2">{title}</span>
      {children}
    </div>
  );
}

/** Inline rejection text, shown under the button that failed (never swallowed). */
function FailNote({ failed, forKey }: { failed: { key: string; message: string } | null; forKey: string }) {
  if (!failed || failed.key !== forKey) return null;
  return <span className="text-[11px] leading-snug text-ed-red">{failed.message}</span>;
}

function LabeledRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[12px] text-ed-text-2">{label}</span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  );
}
